import { Check } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';

/**
 * FlowShell — shared step indicator + back-button chrome used by all four
 * pricing-questionnaire flows. Keeps the header and outer card identical
 * across Learn / Defend / Launch / Reach so the UX feels like one product.
 */
export default function FlowShell({
  kicker,          // "acodia learn" etc.
  title,           // "Find your programme"
  stepLabels,      // ['Course', 'About You', 'Your Plan', 'Confirm']
  step,            // 1-indexed
  onBack,          // function | null
  onExit,          // function that returns to landing
  children,
}) {
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
      {/* Exit to landing */}
      <button
        type="button"
        onClick={onExit}
        className="font-mono text-[10px] uppercase tracking-[0.25em] mb-4"
        style={{ color: 'var(--ts)' }}
        data-testid="flow-exit-to-landing"
      >
        ← Back to services
      </button>

      <div className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
          {kicker}
        </p>
        <h1
          className="font-display font-extrabold tracking-[-0.03em] mt-2"
          style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: 'var(--white)' }}
        >
          {title}
        </h1>
      </div>

      <Stepper step={step} labels={stepLabels} />

      {onBack && step > 1 && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 font-body text-sm mt-6 mb-2"
          style={{ color: 'var(--tm)' }}
          data-testid="flow-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
      )}

      <div className="mt-6">{children}</div>
    </div>
  );
}

function Stepper({ step, labels }) {
  return (
    <div
      data-testid="flow-stepper"
      className="flex items-center justify-between w-full"
      role="list"
    >
      {labels.map((label, i) => {
        const n = i + 1;
        const isDone = n < step;
        const isCurrent = n === step;
        const bg = isDone || isCurrent ? 'var(--a)' : 'transparent';
        const borderColor = isDone || isCurrent ? 'var(--a)' : 'var(--b2)';
        const dotColor = isDone || isCurrent ? 'var(--white)' : 'var(--ts)';
        return (
          <div key={label} className="flex-1 flex items-center" role="listitem">
            <div className="flex flex-col items-center" style={{ minWidth: 72 }}>
              <div
                data-testid={`flow-step-dot-${n}`}
                className="flex items-center justify-center rounded-full transition-colors"
                style={{
                  width: 28,
                  height: 28,
                  background: bg,
                  border: `2px solid ${borderColor}`,
                  color: dotColor,
                }}
              >
                {isDone ? <Check size={12} /> : <span className="font-mono text-xs">{n}</span>}
              </div>
              <span
                className="font-body text-[10px] mt-1.5 text-center"
                style={{
                  color: isCurrent ? 'var(--white)' : isDone ? 'var(--tm)' : 'var(--ts)',
                }}
              >
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div
                className="flex-1 h-[2px] mx-2"
                style={{
                  background: n < step ? 'var(--a)' : 'var(--b1)',
                  marginTop: -18,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
